import React, { useEffect, useRef, useState } from "react";
import "./countdown.css";
import confetti from "canvas-confetti";
import { useGSAP } from "@gsap/react";
import dayjs from "dayjs";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const CONFETTI_COLORS = {
  theme1: ["#D4AF37", "#EAD7A1", "#FFFFFF", "#9AA38B"],
  theme2: ["#703039", "#915F64", "#F7EFE6", "#D4AF37"],
  theme3: ["#FFFFFF", "#E6D9C8", "#4D453D", "#D4AF37"],
  theme4: ["#F7EAD7", "#D4AF37", "#FFFFFF", "#C17B64"],
  theme5: ["#FFFFFF", "#F1EDE4", "#962632", "#D4AF37"],
  theme6: ["#D4AF37", "#FFFFFF", "#C9B8A5", "#8A6F50"],
  theme7: ["#FFFFFF", "#D4AF37", "#B8C9B0", "#EAD7A1"],
  theme8: ["#FFFFFF", "#D4AF37", "#C0A0A8", "#EAD7A1"],
};

gsap.registerPlugin(ScrollTrigger);

const Countdown = ({ details }) => {
  const sectionRef = useRef(null);
  const countdownRef = useRef(null);



  const [heading, setHeading] = useState("Counting down to Forever");

  const canvasRef = useRef(null);
  const confettiRef = useRef(null);
  const hasPlayedCelebration = useRef(false);
const revealRef = useRef(null);

const theme = details?.theme || "theme1";

const confettiColors =
  CONFETTI_COLORS[theme] || CONFETTI_COLORS.theme1;

  const countdownDate = details?.countDownDate;

  const isCountdownCompleted = (time) => {
    return (
      time.days === "00" &&
      time.hours === "00" &&
      time.minutes === "00" &&
      time.seconds === "00"
    );
  };


  const playCelebration = () => {
    if (hasPlayedCelebration.current) return;

    if (!confettiRef.current) return;

    hasPlayedCelebration.current = true;

    // Change heading
    setHeading("Our Forever Begins");

    gsap.fromTo(
      countdownRef.current,
      {
        scale: 0.82,
        rotation: -2,
      },
      {
        scale: 1,
        rotation: 0,
        duration: 0.8,
        ease: "elastic.out(1, 0.45)",
      }
    );


    
    setTimeout(() => {
      const duration = 1200;
      const end = Date.now() + duration;

      (function shootLeft() {
        confettiRef.current({
          particleCount: 3,
          spread: 85,
          startVelocity: 18,
          gravity: 0.45,
          decay: 0.94,
          scalar: 1.05,
          ticks: 100,
          drift: 0.2,
          angle: 60,
          origin: {
            x: 0.18,
            y: 0.92,
          },
          colors: confettiColors,
        });

        if (Date.now() < end) {
          requestAnimationFrame(shootLeft);
        }
      })();

      (function shootRight() {
        confettiRef.current({
          particleCount: 3,
          spread: 85,
          startVelocity: 18,
          gravity: 0.45,
          decay: 0.94,
          scalar: 1.05,
          ticks: 140,
          drift: 0.2,
          angle: 120,
          origin: {
            x: 0.82,
            y: 0.92,
          },
            colors: confettiColors,
        });

        if (Date.now() < end) {
          requestAnimationFrame(shootRight);
        }
      })();
    }, 250);
  };

const calculateTimeLeft = () => {
  if (!countdownDate) {
    return {
      days: "00",
      hours: "00",
      minutes: "00",
      seconds: "00",
    };
  }

  const targetDate = dayjs(countdownDate).endOf("day");
  const difference = targetDate.valueOf() - Date.now();

  if (difference <= 0) {
    return {
      days: "00",
      hours: "00",
      minutes: "00",
      seconds: "00",
    };
  }

  return {
    days: String(
      Math.floor(difference / (1000 * 60 * 60 * 24))
    ).padStart(2, "0"),

    hours: String(
      Math.floor((difference / (1000 * 60 * 60)) % 24)
    ).padStart(2, "0"),

    minutes: String(
      Math.floor((difference / (1000 * 60)) % 60)
    ).padStart(2, "0"),

    seconds: String(
      Math.floor((difference / 1000) % 60)
    ).padStart(2, "0"),
  };
};
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  const [animate, setAnimate] = useState({
    days: false,
    hours: false,
    minutes: false,
    seconds: false,
  });

  const timeoutRef = useRef(null);
  const timeLeftRef = useRef(timeLeft);

  useEffect(() => {
    timeLeftRef.current = timeLeft;
  }, [timeLeft]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        const newTime = calculateTimeLeft();

        setAnimate({
          days: newTime.days !== prev.days,
          hours: newTime.hours !== prev.hours,
          minutes: newTime.minutes !== prev.minutes,
          seconds: newTime.seconds !== prev.seconds,
        });

        clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(() => {
          setAnimate({
            days: false,
            hours: false,
            minutes: false,
            seconds: false,
          });
        }, 250);

        return newTime;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
      clearTimeout(timeoutRef.current);
    };
  }, [countdownDate]);

  useEffect(() => {
    if (!canvasRef.current) return;

    confettiRef.current = confetti.create(canvasRef.current, {
      resize: true,
      useWorker: true,
    });
  }, []);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

       // Entrance animation — separate from celebration
    gsap.fromTo(
      revealRef.current,
      {
        autoAlpha: 0,
        y: 24,
      },
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.8,
        ease: "power2.out",
        clearProps: "transform",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
          once: true,
        },
      }
    );

      mm.add("(max-width: 767px)", () => {
        ScrollTrigger.create({
          trigger: sectionRef.current,
          start: "top 50%",
          once: true,

          onEnter: () => {
            if (isCountdownCompleted(timeLeftRef.current)) {
              playCelebration();
            }
          },
        });
      });

      mm.add("(min-width: 768px)", () => {
        ScrollTrigger.create({
          trigger: sectionRef.current,
          start: "top 45%",
          once: true,

          onEnter: () => {
            if (isCountdownCompleted(timeLeftRef.current)) {
              playCelebration();
            }
          },
        });
      });

      return () => mm.revert();
    },
    {
      scope: sectionRef,
    }
  );

  // If countdown date is empty, don't render anything
  if (!countdownDate) {
    return null;
  }

  return (
    <section
    className={`countdown-section ${details?.theme || ''}`}
      ref={sectionRef}
      
    >
      <canvas
        ref={canvasRef}
        className="countdown-confetti-canvas"
      />
<div ref={revealRef} className="countdown-reveal">
  <h1 >{heading}</h1>
  
      <div
        ref={countdownRef}
        className="countdown-container"
        // data-aos="fade-up"
      >
        <div className="countdown-item">
          <div className="countdown-box">
            <span className={animate.days ? "flip-number" : ""}>
              {timeLeft.days}
            </span>
          </div>
          <p>Days</p>
        </div>

        <div className="countdown-item">
          <div className="countdown-box">
            <span className={animate.hours ? "flip-number" : ""}>
              {timeLeft.hours}
            </span>
          </div>
          <p>Hours</p>
        </div>

        <div className="countdown-item">
          <div className="countdown-box">
            <span className={animate.minutes ? "flip-number" : ""}>
              {timeLeft.minutes}
            </span>
          </div>
          <p>Minutes</p>
        </div>

        <div className="countdown-item">
          <div className="countdown-box">
            <span className={animate.seconds ? "flip-number" : ""}>
              {timeLeft.seconds}
            </span>
          </div>
          <p>Seconds</p>
        </div>
      </div>
</div>
    

    </section>
  );
};

export default Countdown;

